import { useCallback, useRef } from 'react';

interface IUseIntersection {
    isProcessing?: boolean;
    onIntersection?: (observer: IntersectionObserver | null) => void;
    onObserveDisconnect?: (node: Element | null) => void;
    onLeave?: (observer: IntersectionObserver | null) => void;
    root?: Element | null;
}

export const useIntersection = (props: IUseIntersection) => {
    const { isProcessing, onIntersection, onObserveDisconnect, onLeave, root } = props;

    const observer = useRef<IntersectionObserver | null>(null);

    const refCallback = useCallback(
        (node: Element | null) => {
            if (isProcessing) {
                return;
            }

            if (observer.current) {
                observer.current.disconnect();
                onObserveDisconnect?.(node);
            }

            observer.current = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting) {
                        onIntersection?.(observer.current);
                    } else {
                        onLeave?.(observer.current);
                    }
                },
                {
                    root,
                },
            );

            if (node) {
                observer.current.observe(node);
            }
        },
        [isProcessing, onIntersection, onLeave, onObserveDisconnect, root],
    );

    return { refCallback, observer };
};
